import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { MessageContent } from "@/modules/chatbot/components/message-content";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import type { Message as ChatbotMessage } from "@/modules/chatbot/types";
import DOMPurify from "dompurify";

const BUCKET_STYLES: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  Perfect: {
    bg: "bg-emerald-100",
    text: "text-emerald-800",
    label: "Perfect",
  },
  "Good enough": {
    bg: "bg-green-50",
    text: "text-green-700",
    label: "Good enough",
  },
  "Good Enough": {
    bg: "bg-green-50",
    text: "text-green-700",
    label: "Good enough",
  },
  Incomplete: {
    bg: "bg-amber-100",
    text: "text-amber-800",
    label: "Incomplete",
  },
  Incorrect: {
    bg: "bg-red-100",
    text: "text-red-800",
    label: "Incorrect",
  },
};

function getBucketStyle(
  bucket: string | null,
): { bg: string; text: string; label: string } | null {
  if (!bucket) return null;
  const normalized = bucket.trim();
  return (
    BUCKET_STYLES[normalized] ?? {
      bg: "bg-gray-100",
      text: "text-gray-800",
      label: normalized,
    }
  );
}

interface AgentShadowMessageProps {
  message: ChatbotMessage;
}

export const AgentShadowMessage = ({ message }: AgentShadowMessageProps) => {
  const [isAgentResponseOpen, setIsAgentResponseOpen] = useState(true);
  const {
    shadowEval,
    onAnalyticsClick,
    isAnalyticsActive,
    responseNumber,
  } = message;
  const timestamp = message.timestamp
    ? format(message.timestamp, "MMM d, h:mm a")
    : null;

  const handleAnalyticsClick = () => {
    if (onAnalyticsClick) {
      onAnalyticsClick(message.id);
    }
  };

  // Extract data from the shadow evaluation (promoted fields + details)
  const aiResponse = shadowEval?.aiResponse;
  const comparison = shadowEval?.details.comparison;
  const observability = shadowEval?.details.observability;
  const completenessScores = shadowEval?.details.completenessScores;

  const comparisonBucket = shadowEval?.comparisonBucket ?? null;
  const bucketStyle = getBucketStyle(comparisonBucket);
  const aiCompletenessScore = completenessScores?.aiMessageScore ?? null;

  // Extract missing keywords count
  const missingKeywords =
    typeof comparison?.analysis === "object" &&
    comparison.analysis !== null &&
    "missingKeywords" in comparison.analysis
      ? (comparison.analysis.missingKeywords as string[] | undefined)
      : undefined;
  const missingKeywordsCount = missingKeywords?.length || 0;

  // Extract tone classifications
  const aiTone = comparison?.aiTone || observability?.tone?.classification;
  const humanTone = comparison?.humanTone;

  return (
    <div className="flex flex-col mb-4 min-w-0 w-full">
      <div className="flex items-start gap-3 mb-2 min-w-0 w-full justify-end">
        {/* Analytics Button on the Left */}
        <button
          onClick={handleAnalyticsClick}
          className={cn(
            "flex flex-col items-center justify-center place-self-center gap-0.5 px-3 py-2 rounded-lg transition-all duration-200 shrink-0 min-w-[90px]",
          )}
          title={isAnalyticsActive ? "Viewing analytics" : "View analytics"}
        >
          <div className="text-xs font-normal whitespace-nowrap text-gray-800 mb-1">
            Response {responseNumber || 1}
          </div>
          <div className="text-[0.625rem] font-normal whitespace-nowrap bg-gray-300 hover:bg-gray-400 cursor-pointer text-black-100 px-2.5 py-1 rounded-full">
            {isAnalyticsActive ? "Viewing analytics" : "View Analytics"}
          </div>
        </button>

        {/* Message Content */}
        <div
          className="flex-1 max-w-md rounded-[20px] border overflow-hidden bg-white"
          style={{
            borderColor: "var(--color-gray-400)",
          }}
        >
          {/* AI Generated Response Section */}
          {aiResponse && (
            <div className="flex flex-col gap-3 p-4">
              {/* Header with title and metrics */}
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <p className="text-xs font-medium text-violet-600">
                    AI Generated Response
                  </p>
                  <div className="flex gap-3 items-center">
                    {bucketStyle && (
                      <span
                        className={cn(
                          "inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium",
                          bucketStyle.bg,
                          bucketStyle.text,
                        )}
                      >
                        {bucketStyle.label}
                      </span>
                    )}
                    {comparisonBucket && aiCompletenessScore !== null && (
                      <div className="w-px h-3 bg-gray-400" />
                    )}
                    {aiCompletenessScore !== null && (
                      <p className="text-xs">
                        <span className="font-medium text-black-200">
                          {aiCompletenessScore}%
                        </span>{" "}
                        <span className="font-normal text-gray-700">
                          Completeness Score
                        </span>
                      </p>
                    )}
                  </div>
                </div>

                {/* AI Response Content */}
                <div
                  className="text-xs text-black-50 leading-normal whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(aiResponse),
                  }}
                />
              </div>

              {/* Separator */}
              <div className="w-full h-px bg-[repeating-linear-gradient(to_right,#E5E7EB_0_2px,transparent_8px_10px)]" />

              {/* Tags and Missing Keywords */}
              <div className="flex items-center justify-between">
                {/* Tone/Grammar Tags */}
                <div className="flex gap-2 items-center">
                  {aiTone && (
                    <div className="bg-white border border-gray-300 px-2.5 py-1.5 rounded-md">
                      <p className="text-xs font-medium text-black-100 capitalize">
                        {aiTone}
                      </p>
                    </div>
                  )}
                </div>

                {/* Missing Keywords Indicator */}
                {missingKeywordsCount > 0 && (
                  <div className="flex gap-1.5 items-center">
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-200" />
                    <p className="text-[10px] leading-px text-gray-900">
                      {missingKeywordsCount} Missing Phrases/Keywords
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Separator between sections */}
          {aiResponse && (
            <div className="border border-t border-gray-300 w-full" />
          )}

          {/* Agent's Response Section (Collapsible) */}
          <Collapsible
            open={isAgentResponseOpen}
            onOpenChange={setIsAgentResponseOpen}
          >
            <div className="flex flex-col gap-3 p-4">
              {/* Collapsible Trigger */}
              <CollapsibleTrigger className="flex items-center justify-between w-full">
                <p className="text-xs font-medium text-black-200">
                  Agent's Response
                </p>
                <div className="flex items-center justify-center w-6 h-6 cursor-pointer hover:bg-gray-300 rounded-full">
                  <ChevronDown
                    className={cn(
                      "h-5 w-5 text-gray-700 transition-transform duration-200",
                      isAgentResponseOpen ? "rotate-0" : "-rotate-90",
                    )}
                  />
                </div>
              </CollapsibleTrigger>

              {/* Collapsible Content */}
              <CollapsibleContent>
                <div className="flex flex-col gap-3">
                  {/* Agent Message Content with Keyword Highlighting */}
                  <div className="text-xs text-text-quote leading-normal">
                    <MessageContent
                      content={message.content}
                      contentAttributes={message.contentAttributes}
                      additionalAttributes={message.additionalAttributes}
                      contentType={message.contentType}
                      highlightKeywords={missingKeywords}
                    />
                  </div>
                </div>
              </CollapsibleContent>

              {/* Separator */}
              <div className="w-full h-px bg-[repeating-linear-gradient(to_right,#E5E7EB_0_2px,transparent_8px_10px)]" />

              {/* Tags (always visible) */}
              <div className="flex gap-2 items-center">
                {humanTone && (
                  <div className="bg-white border border-gray-300 px-2.5 py-1.5 rounded-md">
                    <p className="text-xs font-medium text-text-prose capitalize">
                      {humanTone}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Collapsible>

          {/* Separator before timestamp */}
          <div className="border border-t border-gray-300 w-full" />

          {/* Timestamp Footer */}
          {timestamp && (
            <div className="flex items-center justify-end px-4 py-3">
              <p className="text-[10px] text-gray-800 text-right whitespace-pre-wrap">
                {timestamp}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
